import React, { useState } from 'react';
import YearLevelBadge, { normalizeLevelYear } from '../components/YearLevelBadge';
import { TIME_SLOTS, isAuditorAvailableForSlot } from '../utils/sessionUtils';

const SESSION_TYPE_OPTIONS = [
  'Orientation',
  'Council 1', 'Council 2', 'Council 3',
  'Council 4', 'Council 5', 'Council 6',
];

function cohortHasSpoc(c) {
  return !!c.spocId;
}

export default function SessionForm({
  sessionFormState,
  sessionFormClashError,
  sessionFormSubmitting = false,
  cohorts,
  allGroups = [],
  sessions,
  auditors,
  counselors = [],
  resources,
  resourceLoadState = 'ready',
  onRetryResources,
  getAutoResources,
  isTypeCompleted,
  setSessionFormState,
  setSessionFormClashError,
  handleCancelSessionForm,
  handleCreateSessionSubmit,
  handleSessionToggleAuditor,
  handleSessionToggleResource,
  checkAuditorClash,
  schools = [],
  lockContext = false,
  isEdit = false,
}) {
  const isGroup = sessionFormState.targetType === 'group';
  const selectedGroup = isGroup ? allGroups.find(g => g.id === sessionFormState.groupId) : null;
  const selectedCohort = !isGroup
    ? cohorts.find((c) => String(c.id) === String(sessionFormState.cohortId))
    : null;
  const programmeYear = sessionFormState.levelYear
    ?? selectedCohort?.levelYear
    ?? selectedGroup?.year
    ?? null;
  const schoolCohorts = (cohorts || []).filter(c => c.status === 'Active' && String(c.schoolId) === String(sessionFormState.schoolId));
  const selectableSchoolCohorts = schoolCohorts.filter(cohortHasSpoc);

  const [auditorSearch, setAuditorSearch] = useState('');
  const [auditorFilter, setAuditorFilter] = useState('available'); // 'available' | 'unavailable' | 'all'

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row" style={{ marginBottom: '24px' }}>
        <div>
          <h1 className="dashboard-title">{isEdit ? 'Edit Fellowship Session' : 'Plan Fellowship Session'}</h1>
          <p className="dashboard-subtitle" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {isGroup && selectedGroup ? (
              <>
                <span>Group: {selectedGroup.name}</span>
                {programmeYear && <YearLevelBadge levelYear={programmeYear} />}
              </>
            ) : sessionFormState.cohortId ? (
              <>
                <span>Cohort: {selectedCohort?.name || `Cohort #${sessionFormState.cohortId}`}</span>
                {programmeYear && <YearLevelBadge levelYear={programmeYear} />}
              </>
            ) : (
              'Select a cohort or group to plan a session.'
            )}
          </p>
        </div>
        <button className="btn-secondary-action" onClick={handleCancelSessionForm}>Cancel</button>
      </div>

      <form onSubmit={handleCreateSessionSubmit} className="session-form-single-card">
        {sessionFormClashError && (
          <div className="clash-error-alert" style={{ marginBottom: '0', marginTop: 0 }}>
            ⚠️ {sessionFormClashError}
          </div>
        )}

        {/* School selector — first field, drives cohort/group scope */}
        <div className="form-field-group">
          <label>Select School</label>
          {lockContext ? (
            <div className="form-control-select" style={{ background: 'var(--neutral-50)', color: 'var(--text-muted)', cursor: 'default' }}>
              {(schools || []).find(s => String(s.id) === String(sessionFormState.schoolId))?.school_name ||
               (schools || []).find(s => String(s.id) === String(sessionFormState.schoolId))?.name ||
               `School #${sessionFormState.schoolId}`}
            </div>
          ) : (
            <select
              id="schoolSelect"
              className="form-control-select"
              value={sessionFormState.schoolId}
              onChange={(e) => {
                setSessionFormState(prev => ({
                  ...prev,
                  schoolId: e.target.value,
                  cohortId: '',
                  groupId: '',
                  levelYear: null,
                  sessionType: 'Orientation',
                  sessionNumber: 1,
                  resources: [],
                  auditors: [],
                }));
                setSessionFormClashError('');
              }}
            >
              <option value="" disabled>-- Select School --</option>
              {(() => {
                const cohortSchoolIds = new Set((cohorts || []).map(c => String(c.schoolId)));
                const groupSchoolIds = new Set((allGroups || []).map(g => String(g.schoolId)));
                return (schools || []).filter(s =>
                  cohortSchoolIds.has(String(s.id)) || groupSchoolIds.has(String(s.id))
                );
              })().map(s => (
                <option key={s.id} value={s.id}>{s.school_name || s.name}</option>
              ))}
            </select>
          )}
        </div>

        {!sessionFormState.schoolId ? (
          <div style={{
            color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic',
            padding: '16px', background: 'var(--neutral-50)', borderRadius: '8px',
            border: '1px solid var(--border-color)', textAlign: 'center'
          }}>
            Please select a school first to load cohort and group options.
          </div>
        ) : (
        <>
        {/* Target type toggle — hidden when locked to cohort context */}
        {!lockContext && (
        <div className="form-field-group">
          <label>Session Target</label>
          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            {['cohort', 'group'].map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setSessionFormState(prev => ({
                  ...prev,
                  targetType: type,
                  cohortId: '',
                  groupId: '',
                  levelYear: null,
                  sessionType: 'Orientation',
                  sessionNumber: 1,
                  resources: [],
                  auditors: [],
                }))}
                style={{
                  padding: '8px 20px', fontSize: '13px', borderRadius: '7px', cursor: 'pointer',
                  border: '1px solid',
                  borderColor: sessionFormState.targetType === type ? 'var(--primary-navy)' : 'var(--border-color)',
                  background: sessionFormState.targetType === type ? 'var(--primary-navy)' : '#fff',
                  color: sessionFormState.targetType === type ? '#fff' : 'var(--neutral-700)',
                  fontWeight: sessionFormState.targetType === type ? '600' : '400',
                }}
              >
                {type === 'cohort' ? 'Single Cohort' : 'Group (multi-cohort)'}
              </button>
            ))}
          </div>
          {isGroup && (
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '6px 0 0', fontStyle: 'italic' }}>
              Group sessions are planned for all cohorts in the group simultaneously.
            </p>
          )}
        </div>
        )}

        {/* Cohort or Group selector */}
        {!isGroup ? (
          <div className="form-field-group">
            <label htmlFor="cohortSelect">Select Cohort</label>
            {lockContext ? (
              <div className="form-control-select" style={{ background: 'var(--neutral-50)', color: 'var(--text-muted)', cursor: 'default' }}>
                {cohorts.find(c => c.id === sessionFormState.cohortId)?.name || `Cohort #${sessionFormState.cohortId}`}
              </div>
            ) : (
              <>
                <select
                  id="cohortSelect"
                  className="form-control-select"
                  value={sessionFormState.cohortId}
                  onChange={(e) => {
                    const selectedCohortId = e.target.value;
                    const cohortSessions = sessions.filter(s => s.cohortId === selectedCohortId);
                    const orientationCompleted = cohortSessions.some(s => s.sessionType === 'Orientation');
                    let nextType = 'Orientation';
                    let nextNumber = 1;

                    if (orientationCompleted) {
                      const councilNumbers = cohortSessions
                        .filter(s => s.sessionType.startsWith('Council'))
                        .map(s => parseInt(s.sessionType.split(' ')[1]) || 0);
                      const maxCouncil = councilNumbers.length > 0 ? Math.max(...councilNumbers) : 0;
                      nextNumber = maxCouncil + 1;
                      nextType = `Council ${nextNumber}`;
                    }

                    const selectedCohort = cohorts.find(c => c.id === selectedCohortId);
                    const levelYear = selectedCohort?.levelYear ?? null;
                    const autoResources = getAutoResources(nextType, levelYear);

                    setSessionFormState(prev => ({
                      ...prev,
                      cohortId: selectedCohortId,
                      levelYear,
                      sessionType: nextType,
                      sessionNumber: nextNumber,
                      resources: autoResources,
                      auditors: []
                    }));
                    setSessionFormClashError('');
                  }}
                >
                  <option value="" disabled>-- Select Cohort --</option>
                  {schoolCohorts.map(c => {
                    const studentCount = Number.isFinite(Number(c.studentCount))
                      ? Number(c.studentCount)
                      : (c.studentIds || []).length;
                    const hasSpoc = cohortHasSpoc(c);
                    const yearLabel = c.levelYear ? ` · ${normalizeLevelYear(c.levelYear)}` : '';
                    return (
                      <option key={c.id} value={c.id} disabled={!hasSpoc}>
                        {hasSpoc ? `${c.name} (${studentCount})${yearLabel}` : `${c.name} (Assign SPOC first)`}
                      </option>
                    );
                  })}
                </select>
                {selectableSchoolCohorts.length === 0 && schoolCohorts.length > 0 && (
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)', marginTop: '10px' }}>
                    No cohorts in this school have a SPOC assigned yet.
                  </div>
                )}
              </>
            )}
          </div>

        ) : (
          <div className="form-field-group">
            <label htmlFor="groupSelect">Select Group</label>
            {allGroups.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                No groups created yet. Create groups in the Schools module first.
              </div>
            ) : (
              <select
                id="groupSelect"
                className="form-control-select"
                value={sessionFormState.groupId}
                onChange={(e) => {
                  const selectedGroupId = e.target.value;
                  const group = allGroups.find(g => g.id === selectedGroupId);
                  const levelYear = group?.year ?? null;
                  setSessionFormState(prev => ({
                    ...prev,
                    groupId: selectedGroupId,
                    levelYear,
                    sessionType: 'Orientation',
                    sessionNumber: 1,
                    resources: getAutoResources('Orientation', levelYear),
                    auditors: [],
                  }));
                  setSessionFormClashError('');
                }}
              >
                <option value="" disabled>-- Select Group --</option>
                {allGroups.filter(g => String(g.schoolId) === String(sessionFormState.schoolId)).map(g => (
                  <option key={g.id} value={g.id}>{g.name} · {g.year}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <hr className="form-section-divider" style={{ margin: '8px 0' }} />

        {(isGroup ? sessionFormState.groupId : sessionFormState.cohortId) && programmeYear && (
          <div style={{
            marginBottom: '16px',
            padding: '12px 16px',
            background: programmeYear === 'Year 2' ? 'rgba(30, 56, 114, 0.06)' : 'rgba(1, 71, 162, 0.05)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}>
            <span style={{ color: 'var(--neutral-700)' }}>Programme year for this session:</span>
            <YearLevelBadge levelYear={programmeYear} />
            <span style={{ color: 'var(--text-muted)' }}>
              Resources below are matched to {normalizeLevelYear(programmeYear) || 'Unassigned'} Glosco materials.
            </span>
          </div>
        )}

        {(isGroup ? !sessionFormState.groupId : !sessionFormState.cohortId) ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '16px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
            {isGroup ? 'Please select a group first.' : 'Please select a student cohort first to load sequence configurations.'}
          </div>
        ) : (
          <>
            <div className="form-field-group">
              <label htmlFor="sessionType">Session Type</label>
              {isEdit ? (
                <div className="form-control-select" style={{ background: 'var(--neutral-50)', color: 'var(--text-muted)', cursor: 'default' }}>
                  {sessionFormState.sessionType}
                </div>
              ) : (
                <select
                  id="sessionType"
                  className="form-control-select"
                  value={sessionFormState.sessionType}
                  onChange={(e) => {
                    const val = e.target.value;
                    const match = val.match(/\d+/);
                    const num = match ? parseInt(match[0]) : 1;
                    const autoRes = getAutoResources(val, sessionFormState.levelYear ?? null);
                    setSessionFormState(prev => ({
                      ...prev,
                      sessionType: val,
                      sessionNumber: val === 'Orientation' ? 1 : num,
                      resources: autoRes
                    }));
                  }}
                >
                  {SESSION_TYPE_OPTIONS.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-field-group">
              <label htmlFor="scheduledAt">Date</label>
              <input
                id="scheduledAt"
                type="date"
                className="form-control-input"
                value={sessionFormState.scheduledAt}
                onChange={(e) => {
                  setSessionFormState(prev => ({ ...prev, scheduledAt: e.target.value, auditors: [] }));
                  setSessionFormClashError('');
                }}
                required
              />
            </div>

            <div className="form-field-group">
              <label htmlFor="timeSlot">Time Slot</label>
              <select
                id="timeSlot"
                className="form-control-select"
                value={sessionFormState.timeSlot}
                onChange={(e) => {
                  setSessionFormState(prev => ({ ...prev, timeSlot: e.target.value, auditors: [] }));
                  setSessionFormClashError('');
                }}
                required
              >
                <option value="" disabled>-- Select Time Slot --</option>
                {TIME_SLOTS.map(slot => (
                  <option key={slot} value={slot}>{slot}</option>
                ))}
              </select>
            </div>

            <div className="form-field-group">
              <label htmlFor="meetingLink">Meeting Link</label>
              <input
                id="meetingLink"
                type="url"
                className="form-control-input"
                placeholder="https://zoom.us/j/..."
                value={sessionFormState.meetingLink}
                onChange={(e) => setSessionFormState(prev => ({ ...prev, meetingLink: e.target.value }))}
                required
              />
            </div>

            <div className="form-field-group">
              <label htmlFor="comments">Comments (Optional)</label>
              <textarea
                id="comments"
                className="form-control-textarea"
                rows="3"
                placeholder="Provide session briefing notes for SPOC & Auditors..."
                value={sessionFormState.comments}
                onChange={(e) => setSessionFormState(prev => ({ ...prev, comments: e.target.value }))}
              ></textarea>
            </div>

            <hr className="form-section-divider" style={{ margin: '24px 0' }} />

            {/* Auto-attached resources (read-only) */}
            <div className="form-field-group">
              {(() => {
                const autoResourceObjects = (sessionFormState.resources || [])
                  .map(id => resources.find(r => r.id === id))
                  .filter(Boolean);
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
                      <label style={{ margin: 0 }}>Session Resources</label>
                      <span style={{ fontSize: '12px', color: 'var(--primary-navy)', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {autoResourceObjects.length} auto-attached for {sessionFormState.sessionType}
                        {programmeYear && <YearLevelBadge levelYear={programmeYear} />}
                      </span>
                    </div>
                    {resourceLoadState === 'error' ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '14px 16px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                        <span>Resources could not be loaded yet.</span>
                        {onRetryResources && <button type="button" onClick={onRetryResources} style={{ border: '1px solid var(--primary-navy)', borderRadius: '6px', background: '#fff', color: 'var(--primary-navy)', padding: '6px 10px', fontWeight: 700, cursor: 'pointer' }}>Retry</button>}
                      </div>
                    ) : autoResourceObjects.length === 0 ? (
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '14px 16px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                        {resourceLoadState === 'error'
                          ? 'Resources could not be loaded. Retry the workspace before creating this session.'
                          : resourceLoadState === 'loading'
                            ? 'Loading resources…'
                            : 'No resources matched for this session type and cohort level.'}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {autoResourceObjects.map(res => (
                          <div
                            key={res.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '10px 14px',
                              background: 'rgba(1, 71, 162, 0.03)',
                              border: '1px solid rgba(1, 71, 162, 0.18)',
                              borderRadius: '6px',
                            }}
                          >
                            <span style={{ fontSize: '18px', lineHeight: 1 }}>
                              {res.fileType === 'ppt' ? '📊' : res.fileType === 'pdf' ? '📄' : res.fileType === 'mp4' ? '🎬' : '📎'}
                            </span>
                            <span style={{ flexGrow: 1, fontSize: '14px', color: 'var(--neutral-800)', fontWeight: 500 }}>{res.title}</span>
                            {res.levelName && <YearLevelBadge levelYear={res.levelName} />}
                            <span className="checklist-row-badge" style={{ margin: 0, flexShrink: 0 }}>{res.fileType.toUpperCase()}</span>
                            {res.viewUrl && (
                              <a href={res.viewUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', textDecoration: 'underline', flexShrink: 0 }} onClick={e => e.stopPropagation()}>Preview</a>
                            )}
                            {res.downloadUrl && (
                              <a href={res.downloadUrl} download style={{ fontSize: '13px', textDecoration: 'underline', flexShrink: 0 }} onClick={e => e.stopPropagation()}>Download</a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Custom links */}
            <div className="form-field-group" style={{ marginTop: '20px' }}>
              <label style={{ marginBottom: '10px' }}>Additional Links</label>
              {(sessionFormState.customLinks || []).map((link, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                  <input
                    type="url"
                    className="form-control-input"
                    style={{ flexGrow: 1 }}
                    placeholder="https://drive.google.com/..."
                    value={link.url}
                    onChange={e => {
                      const updated = [...sessionFormState.customLinks];
                      updated[idx] = { ...updated[idx], url: e.target.value };
                      setSessionFormState(prev => ({ ...prev, customLinks: updated }));
                    }}
                  />
                  <input
                    type="text"
                    className="form-control-input"
                    style={{ width: '160px', flexShrink: 0 }}
                    placeholder="Label (optional)"
                    value={link.label}
                    onChange={e => {
                      const updated = [...sessionFormState.customLinks];
                      updated[idx] = { ...updated[idx], label: e.target.value };
                      setSessionFormState(prev => ({ ...prev, customLinks: updated }));
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setSessionFormState(prev => ({
                      ...prev,
                      customLinks: prev.customLinks.filter((_, i) => i !== idx)
                    }))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '18px', lineHeight: 1, padding: '0 4px' }}
                    title="Remove link"
                  >×</button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setSessionFormState(prev => ({
                  ...prev,
                  customLinks: [...(prev.customLinks || []), { url: '', label: '' }]
                }))}
                style={{
                  marginTop: '4px',
                  background: 'none',
                  border: '1px dashed var(--neutral-400)',
                  borderRadius: '6px',
                  color: 'var(--primary-navy)',
                  fontSize: '13px',
                  fontWeight: 500,
                  padding: '6px 14px',
                  cursor: 'pointer'
                }}
              >+ Add link</button>
            </div>

            {/* Counselor selector — single-select, scoped to cohort's counselors */}
            <div className="form-field-group">
              <label htmlFor="counselorSelect">Assign Counselor (Optional)</label>
              <select
                id="counselorSelect"
                className="form-control-select"
                value={sessionFormState.counselorId}
                onChange={(e) => {
                  setSessionFormState(prev => ({ ...prev, counselorId: e.target.value }));
                }}
              >
                <option value="">-- None (No Counselor) --</option>
                {(counselors || []).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '6px 0 0', fontStyle: 'italic' }}>
                All active counselors in the system are shown — no cohort-membership restriction.
              </p>
            </div>

            <hr className="form-section-divider" style={{ margin: '24px 0' }} />

            <div className="form-field-group">
              <label style={{ marginBottom: '16px' }}>Assign Auditors</label>
              {!sessionFormState.scheduledAt || !sessionFormState.timeSlot ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '16px', background: 'var(--neutral-50)', borderRadius: '8px', border: '1px solid var(--border-color)', textAlign: 'center' }}>
                  Please select a Date and Time Slot first to load auditor availability.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="Search auditors by name or email..."
                      value={auditorSearch}
                      onChange={(e) => setAuditorSearch(e.target.value)}
                      style={{
                        flex: '1 1 240px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        fontSize: '13px',
                      }}
                    />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[
                        { key: 'available', label: 'Available' },
                        { key: 'unavailable', label: 'Unavailable' },
                        { key: 'all', label: 'All' },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setAuditorFilter(key)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '999px',
                            fontSize: '12.5px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            border: '1px solid',
                            borderColor: auditorFilter === key ? 'var(--primary-navy)' : 'var(--border-color)',
                            background: auditorFilter === key ? 'var(--primary-navy)' : 'transparent',
                            color: auditorFilter === key ? '#fff' : 'var(--neutral-600)',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const slot = sessionFormState.timeSlot;
                    const searchLower = auditorSearch.trim().toLowerCase();

                    const targetDate = (sessionFormState.scheduledAt || '').split('T')[0];
                    const activeStatuses = ['Planned', 'Live'];

                    const rows = auditors
                      .map(aud => {
                        // Day + time: free only if that weekday has this slot (Ritika day-based availability).
                        const isAvailable = isAuditorAvailableForSlot(
                          aud.availabilitySlots,
                          slot,
                          sessionFormState.scheduledAt || null
                        );
                        const hasClash = checkAuditorClash(aud.id, sessionFormState.scheduledAt, slot, sessionFormState.editingSessionId || null);

                        const auditorSessions = (sessions || []).filter(s => {
                          if (sessionFormState.editingSessionId && String(s.id) === String(sessionFormState.editingSessionId)) return false;
                          const sessionAuditorIds = [
                            ...(s.auditors || []),
                            ...(s.auditorAssignments || []).map(a => a.auditorId),
                          ].map(String);
                          return activeStatuses.includes(s.status) && sessionAuditorIds.includes(String(aud.id));
                        });
                        const sessionLoad = auditorSessions.length;
                        const isSelected = sessionFormState.auditors.map(String).includes(String(aud.id));
                        const hasSameDayDifferentSlot = !hasClash && targetDate && auditorSessions.some(s => {
                          const sDate = (s.scheduledAt || '').split(/[T ]/)[0];
                          return sDate === targetDate;
                        });

                        return { aud, isAvailable, hasClash, sessionLoad, auditorSessions, hasSameDayDifferentSlot, isSelected };
                      })
                      .filter(({ aud }) => {
                        if (!searchLower) return true;
                        return aud.name.toLowerCase().includes(searchLower) || (aud.email || '').toLowerCase().includes(searchLower);
                      })
                      .filter(({ isAvailable }) => {
                        if (auditorFilter === 'all') return true;
                        if (auditorFilter === 'available') return isAvailable;
                        return !isAvailable;
                      })
                      .sort((a, b) => {
                        if (a.isSelected !== b.isSelected) return a.isSelected ? -1 : 1;
                        if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
                        return a.sessionLoad - b.sessionLoad;
                      });

                    if (rows.length === 0) {
                      return (
                        <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                          No auditors match this search/filter.
                        </div>
                      );
                    }

                    return (
                <div className="auditors-availability-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {rows.map(({ aud, isAvailable, hasClash, sessionLoad, auditorSessions, hasSameDayDifferentSlot, isSelected }) => {

                    let statusClass = '';
                    let statusLabel = '';
                    let showClashError = false;

                    if (hasClash) {
                      statusClass = 'has-clash';
                      statusLabel = 'Clash';
                      showClashError = true;
                    } else if (!isAvailable) {
                      statusClass = 'unavailable';
                      statusLabel = 'Unavailable';
                    } else {
                      statusClass = 'available';
                      statusLabel = 'Available';
                    }

                    return (
                      <div
                        key={aud.id}
                        className={`auditor-avail-row ${statusClass}`}
                        style={{
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                          backgroundColor: hasClash
                            ? 'rgba(239, 68, 68, 0.05)'
                            : (!isAvailable ? 'var(--neutral-50)' : 'rgba(16, 185, 129, 0.05)'),
                          borderColor: hasClash
                            ? 'var(--accent-red)'
                            : (!isAvailable ? 'var(--neutral-200)' : 'rgba(16, 185, 129, 0.2)'),
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 700, color: 'var(--neutral-800)', fontSize: '14px' }}>{aud.name}</span>
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{aud.email}</span>
                            <span style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {isSelected ? 'Assigned to this session' : sessionLoad === 0 ? 'No current sessions' : `${sessionLoad} active session${sessionLoad > 1 ? 's' : ''}`}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span
                              style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                backgroundColor: hasClash
                                  ? 'rgba(239, 68, 68, 0.1)'
                                  : (!isAvailable ? 'var(--neutral-200)' : 'rgba(16, 185, 129, 0.1)'),
                                color: hasClash
                                  ? 'var(--accent-red)'
                                  : (!isAvailable ? 'var(--neutral-600)' : '#10b981')
                              }}
                            >
                              {statusLabel}
                            </span>
                            <button
                              type="button"
                              className={`btn-assign-auditor ${isSelected ? 'remove' : 'assign'}`}
                              disabled={hasClash}
                              onClick={() => handleSessionToggleAuditor(aud.id)}
                              style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                fontWeight: 700,
                                fontSize: '13px',
                                cursor: hasClash ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {isSelected ? 'Remove' : 'Assign'}
                            </button>
                          </div>
                        </div>
                        {showClashError && (
                          <div style={{ color: 'var(--accent-red)', fontSize: '12.5px', fontWeight: 600, marginTop: '4px' }}>
                            ⚠️ This auditor is already assigned to another session in this time slot
                          </div>
                        )}
                        {!showClashError && hasSameDayDifferentSlot && (
                          <div style={{ color: '#b45309', fontSize: '12.5px', fontWeight: 600, marginTop: '4px' }}>
                            ⚠️ This auditor already has another session on the same day (different time slot)
                          </div>
                        )}
                        {auditorSessions.length > 0 && (
                          <div style={{ color: 'var(--neutral-600)', fontSize: '12px', marginTop: '2px' }}>
                            Cross-check: {auditorSessions.slice(0, 3).map(s => `${s.sessionType || 'Session'} · ${s.cohortName || s.groupName || '—'} · ${(s.scheduledAt || '').split(/[T ]/)[0] || '—'}`).join(' | ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                    );
                  })()}
                </>
              )}
            </div>

            <hr className="form-section-divider" style={{ margin: '24px 0' }} />

            <button
              type="submit"
              className="btn-submit-session"
              disabled={sessionFormSubmitting || !sessionFormState.scheduledAt || !sessionFormState.timeSlot}
            >
              {sessionFormSubmitting
                ? (isEdit ? 'Saving…' : 'Creating session…')
                : (isEdit ? 'Save Changes' : 'Create Session')}
            </button>
          </>
        )}
        </>
        )}
      </form>
    </div>
  );
}
